/**
 * The three kinds of team member an operator picks between, and what each one
 * implies.
 *
 * This mirrors `app/services/user_archetypes.py`. The backend is authoritative
 * for what is actually written; this copy exists so the dialog can show the
 * consequences of a choice without a round-trip per click.
 */

import type { UserArchetype } from '../../../services/api';

export interface ArchetypeOption {
  value: UserArchetype;
  label: string;
  /** What this person does, in the operator's words -- not in terms of roles. */
  description: string;
  /** Clinics decide where they can be scheduled, so clinicians must pick some. */
  needsClinics: boolean;
  /** Titles decide which visits they can take. */
  needsTitles: boolean;
  /** Whether a Roger App account is offered at all. */
  offersAppAccount: boolean;
  /** Whether the operator picks agency roles directly for this archetype. */
  picksAgencyRoles: boolean;
}

export const ARCHETYPE_OPTIONS: ArchetypeOption[] = [
  {
    value: 'FIELD_CLINICIAN',
    label: 'Field clinician',
    // Deliberately says nothing about the app: a contractor field clinician is
    // still a field clinician, and whether they get an app account follows from
    // their employment type rather than from what they do.
    description: 'Sees patients and documents visits',
    needsClinics: true,
    needsTitles: true,
    offersAppAccount: true,
    picksAgencyRoles: false,
  },
  {
    value: 'CLINICAL_SUPERVISOR',
    label: 'Clinical supervisor',
    // A superset of the field clinician: they carry visits too. Describing
    // them only as a reviewer made the card read as an office job.
    description: 'Sees patients, plus reviews and approves charts',
    needsClinics: true,
    needsTitles: true,
    offersAppAccount: true,
    picksAgencyRoles: false,
  },
  {
    value: 'BACK_OFFICE',
    label: 'Back office',
    description: 'Billing, intake, scheduling — no patient visits',
    needsClinics: false,
    needsTitles: false,
    offersAppAccount: false,
    picksAgencyRoles: true,
  },
];

/**
 * Roles an archetype always grants, shown but never editable.
 *
 * Mirrors the blocking tier in `app/services/user_archetypes.py`. These decide
 * whether somebody can be scheduled, so they stay in code -- making them
 * configurable would recreate the bug this work exists to remove, with the blast
 * radius of every future user. Listed here only so a configuration screen can
 * show what is fixed alongside what is not.
 */
export const ARCHETYPE_GUARANTEED_ROLES: Record<
  UserArchetype,
  readonly string[]
> = {
  FIELD_CLINICIAN: ['Member', 'Assignable as Clinician'],
  CLINICAL_SUPERVISOR: [
    'Member',
    'Assignable as Clinician',
    'Assignable as Case Manager',
  ],
  BACK_OFFICE: ['Member'],
};

/**
 * The clinic-scoped roles each archetype grants, mirroring the `clinic_role_slugs`
 * in `app/services/user_archetypes.py`.
 *
 * These are the blocking tier and stay in code on both sides, so a mirror is
 * safe here in a way the discretionary agency roles are not -- those are
 * per-agency configuration and have to be read from `/agency-config`.
 */
export const ARCHETYPE_CLINIC_ROLE_SLUGS: Record<
  UserArchetype,
  readonly string[]
> = {
  FIELD_CLINICIAN: ['clinic:clinician'],
  CLINICAL_SUPERVISOR: ['clinic:clinician', 'clinic:case-manager'],
  BACK_OFFICE: [],
};

export function archetypeOption(value: UserArchetype): ArchetypeOption {
  const found = ARCHETYPE_OPTIONS.find(option => option.value === value);
  if (!found) {
    throw new Error(`No archetype option for ${value}`);
  }
  return found;
}

/**
 * Agency roles that make somebody a clinician, used only to count how many
 * people already hold each archetype. Shown on the cards because a head-count is
 * the strongest hint that you are on the right one -- and makes a wrong pick
 * look wrong.
 */
const ARCHETYPE_ROLE_SLUGS: Record<UserArchetype, readonly string[]> = {
  FIELD_CLINICIAN: ['emr-field-clinician'],
  CLINICAL_SUPERVISOR: ['clinical-supervisor'],
  BACK_OFFICE: [],
};

export function countMembersByArchetype(
  members:
    | readonly { role_assignments?: { slug: string }[] | null }[]
    | null
    | undefined
): Record<UserArchetype, number> {
  const counts: Record<UserArchetype, number> = {
    FIELD_CLINICIAN: 0,
    CLINICAL_SUPERVISOR: 0,
    BACK_OFFICE: 0,
  };

  for (const member of members ?? []) {
    const slugs = new Set((member.role_assignments ?? []).map(r => r.slug));
    let matched = false;
    for (const option of ARCHETYPE_OPTIONS) {
      if (option.value === 'BACK_OFFICE') continue;
      if (ARCHETYPE_ROLE_SLUGS[option.value].some(slug => slugs.has(slug))) {
        counts[option.value] += 1;
        matched = true;
      }
    }
    // Anyone holding a role but none of the clinician ones is back office. A
    // member with no roles at all is still being set up and is not counted.
    if (!matched && slugs.size > 0) {
      counts.BACK_OFFICE += 1;
    }
  }

  return counts;
}
